import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../../components/card/card.jsx';
import { NavScrollTo } from '../../components/nav-scroll-to/nav-scroll-to.jsx';
import { useDispatch, useSelector } from 'react-redux';
import {
  changePassword,
  clearChangePasswordError,
  fetchUsers,
  updateUserFlag,
  deleteUserByEmail,
  createUser,
  notifyUserCredentials,
  fetchRoles,
  updateUserRole,
  fetchPermissions,
  fetchRolePermissions,
  replaceRolePermissions,
  createRole,  
  deleteRole,
} from '../../actions/auth_actions';
import { Toast as BootstrapToast, Modal as BootstrapModal } from 'bootstrap';

import { Modal, Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';


function Settings() {
  // --- hooks first
  const dispatch = useDispatch();

  // --- selectors next (avoid TDZ)
  const users = useSelector((state) => state.auth.users ?? []);
  const usersLoading = useSelector((state) => state.auth.usersLoading);
  const usersError = useSelector((state) => state.auth.usersError);

  const roles = useSelector((state) => state.auth.roles || []);
  const rolesLoading = useSelector((state) => state.auth.rolesLoading);
  const updatingRoleEmail = useSelector((state) => state.auth.updatingRoleEmail);
  const updateRoleError = useSelector((state) => state.auth.updateRoleError);

  const changepassword_info = useSelector((state) => state.auth.changepassword_info);

  const permissionsMaster = useSelector((s) => s.auth.permissionsMaster || []);
  const permissionsMasterLoading = useSelector((s) => s.auth.permissionsMasterLoading);
  const permissionsMasterError = useSelector((s) => s.auth.permissionsMasterError);

  const rolePermissions = useSelector((s) => s.auth.rolePermissions || []);
  const rolePermissionsLoading = useSelector((s) => s.auth.rolePermissionsLoading);
  const rolePermissionsError = useSelector((s) => s.auth.rolePermissionsError);

  const replacingRolePermissions = useSelector((s) => s.auth.replacingRolePermissions);

  // NEW: track deletion progress/error for roles
  const deletingRoleId = useSelector((s) => s.auth.deletingRoleId);
  const deleteRoleError = useSelector((s) => s.auth.deleteRoleError);


  // --- persistent/local values
  const adminFlag = (() => {
    const stored = localStorage.getItem('adminFlag');
    return stored ? JSON.parse(stored) : 0;
  })();
  const loggedEmail = localStorage.getItem('email');

  // --- local state AFTER selectors
  const [emailToDelete, setEmailToDelete] = useState(null);
  const deleteModalRef = useRef(null);

  const [roleId, setRoleId] = useState(''); // create-user modal role

  // Role-permissions editor state
  const [permsRoleId, setPermsRoleId] = useState('');       // selected role id (string)
  const [permsSelection, setPermsSelection] = useState([]); // number[]

  const [hoveredEmail, setHoveredEmail] = useState(null);

  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPassword2, setShowNewPassword2] = useState(false);

  const [toastMsg, setToastMsg] = useState('');
  const toastElemRef = useRef(null);

  // --- Create Role modal state
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [roleName, setRoleName] = useState('');
  const [roleDesc, setRoleDesc] = useState('');
  const [creatingRole, setCreatingRole] = useState(false);

  // --- Delete Role modal state
  const deleteRoleModalRef = useRef(null);
  const [roleToDelete, setRoleToDelete] = useState(null); // { id:number, name:string } | null

  // --- helpers that depend on selectors
  const roleNameById = (id) =>
    (roles.find((r) => Number(r.id) === Number(id))?.name) || `#${id}`;

  const selectedRoleDesc =
    roles.find(r => Number(r.id) === Number(permsRoleId))?.description?.trim() || '';

  // --- effects
  useEffect(() => {
    if (!roleId && roles && roles.length) {
      setRoleId(String(roles[0].id));
    }
  }, [roles, roleId]);

  useEffect(() => {
    if (adminFlag === 1 && roles.length && !permsRoleId) {
      setPermsRoleId(String(roles[0].id));
    }
  }, [adminFlag, roles, permsRoleId]);

  useEffect(() => {
    if (adminFlag === 1) {
      dispatch(fetchUsers());
      dispatch(fetchRoles());
      dispatch(fetchPermissions());
    }
  }, [adminFlag, dispatch]);

  useEffect(() => {
    if (adminFlag === 1 && permsRoleId) {
      dispatch(fetchRolePermissions(Number(permsRoleId)));
    }
  }, [adminFlag, permsRoleId, dispatch]);

  useEffect(() => {
    if (Array.isArray(rolePermissions)) {
      setPermsSelection(rolePermissions.map((p) => Number(p.id)));
    }
  }, [rolePermissions]);

  useEffect(() => {
    if (changepassword_info) {
      setToastMsg(changepassword_info);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
    }
  }, [changepassword_info]);

  useEffect(() => {
    const handleHide = () => {
      setToastMsg('');
      dispatch(clearChangePasswordError());
    };
    const elem = toastElemRef.current;
    if (elem) elem.addEventListener('hidden.bs.toast', handleHide);
    return () => {
      if (elem) elem.removeEventListener('hidden.bs.toast', handleHide);
    };
  }, [dispatch]);

  // --- validators & helpers
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
  const isValidPassword = (value) => {
    const v = value || '';
    return v.length >= 8 && /[A-Za-z]/.test(v) && /\d/.test(v);
  };
  const generatePassword = (length = 8) => {
    const U = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const L = 'abcdefghijklmnopqrstuvwxyz';
    const D = '0123456789';
    if (length < 8) length = 8;
    const all = U + L + D;
    const pick = (s) => s[Math.floor(Math.random() * s.length)];
    const required = [pick(U), pick(L), pick(D)];
    const rest = Array.from({ length: length - required.length }, () => pick(all));
    const chars = required.concat(rest);
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    return chars.join('');
  };

  // --- handlers
  const handleNotify = (email) => {
    dispatch(notifyUserCredentials(email))
      .then((msg) => {
        setToastMsg(msg || 'Credentials email containing a new password has been sent.');
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      })
      .catch((err) => {
        const message = err?.response?.data?.message || err.message || 'Failed to send notification email.';
        setToastMsg(message);
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      });
  };

  const openCreateUserModal = () => setShowCreateUserModal(true);
  const closeCreateUserModal = () => {
    setEmail('');
    setPassword('');
    setShowCreateUserModal(false);
  };

  const handleCreateUser = async () => {
    if (!isValidEmail(email)) {
      setToastMsg('Please enter a valid email address.');
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }
    if (!isValidPassword(password)) {
      setToastMsg('Password must be at least 8 characters and contain letters and numbers.');
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }
    if (!roleId) {
      setToastMsg('Please select a role.');
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }

    try {
      setSubmitting(true);
      await dispatch(createUser(email, password, Number(roleId)));
      await dispatch(fetchUsers());
      const rName = roleNameById(Number(roleId));
      setToastMsg(`User ${email} created successfully with role "${rName}".`);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();

      setEmail('');
      setPassword('');
      setRoleId(roles.length ? String(roles[0].id) : '');
      closeCreateUserModal();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to create user. Please try again.';
      setToastMsg(message);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
    } finally {
      setSubmitting(false);
    }
  };

  const formValid = isValidEmail(email) && isValidPassword(password) && !!roleId;

  const handleChangePassword = () => {
    dispatch(changePassword(currentPassword, newPassword, newPassword2));
    setCurrentPassword('');
    setNewPassword('');
    setNewPassword2('');
  };

  const handleToggle = (userId, email, currentFlag) => {
    if (email === loggedEmail) {
      const msg = 'You cannot deactivate your own account.';
      setToastMsg(msg);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }
    const newFlag = currentFlag === 1 ? 0 : 1;
    dispatch(updateUserFlag(userId, newFlag))
      .then(() => {
        const msg = `${email} has been successfully ${newFlag === 1 ? 'activated' : 'deactivated'}`;
        setToastMsg(msg);
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      })
      .catch((err) => {
        const errMsg = err.message || 'Failed to update user status';
        setToastMsg(errMsg);
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      });
  };

  const openDeleteModal = (email) => {
    if (email === loggedEmail) {
      const msg = 'You cannot delete your own account.';
      setToastMsg(msg);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }
    setEmailToDelete(email);
    if (deleteModalRef.current) new BootstrapModal(deleteModalRef.current).show();
  };

  const hideDeleteModal = () => {
    if (deleteModalRef.current) {
      const modalInstance =
        BootstrapModal.getInstance(deleteModalRef.current) || new BootstrapModal(deleteModalRef.current);
      modalInstance.hide();
    }
    setEmailToDelete(null);
  };

  const handleRoleChange = (email, currentRoleId, e) => {
    const nextId = Number(e.target.value);
    if (nextId === Number(currentRoleId)) return;

    dispatch(updateUserRole(email, nextId))
      .then(() => {
        const rName = roleNameById(nextId);
        setToastMsg(`Role "${rName}" assigned to user ${email} successfully.`);
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      })
      .catch((err) => {
        const message = err?.response?.data?.message || err.message || 'Failed to update user role';
        setToastMsg(message);
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      });
  };

  const handleConfirmDelete = () => {
    if (!emailToDelete) return;
    dispatch(deleteUserByEmail(emailToDelete))
      .then(() => {
        const msg = `${emailToDelete} has been deleted.`;
        setToastMsg(msg);
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
        hideDeleteModal();
      })
      .catch((err) => {
        const errMsg = err?.message || 'Failed to delete user';
        setToastMsg(errMsg);
        if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      });
  };

  // Role-permissions editor handlers
  const handlePermToggle = (pid) => {
    setPermsSelection((prev) => {
      const id = Number(pid);
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  };

  const handleSaveRolePerms = async () => {
    try {
      const rid = Number(permsRoleId);
      await dispatch(replaceRolePermissions(rid, permsSelection));
      const rName = roleNameById(rid);
      setToastMsg(`Permissions updated for role "${rName}".`);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to update role permissions';
      setToastMsg(message);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
    }
  };

  // Create Role modal handlers
  const openCreateRoleModal = () => {
    setRoleName('');
    setRoleDesc('');
    setShowCreateRoleModal(true);
  };
  const closeCreateRoleModal = () => {
    setShowCreateRoleModal(false);
  };
  const handleCreateRole = async () => {
    const name = roleName.trim();
    const description = roleDesc.trim();
    if (!name) {
      setToastMsg('Please enter a role name.');
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }
    try {
      setCreatingRole(true);
      const created = await dispatch(createRole(name, description)); // { id, name, description }
      const newRoleId = Number(created?.id);
      if (newRoleId) {
        setPermsRoleId(String(newRoleId));
        setPermsSelection([]); // let the user select from scratch
      }
      setToastMsg(`Role "${name}" created. Now select its permissions.`);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      closeCreateRoleModal();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to create role';
      setToastMsg(message);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
    } finally {
      setCreatingRole(false);
    }
  };

  // Delete Role handlers (modal + confirm)
  const openDeleteRoleModal = () => {
    if (!permsRoleId) {
      setToastMsg('Please select a role first.');
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }
    const rid = Number(permsRoleId);
    if (rid === 1) {
      setToastMsg('The "All Access" role cannot be deleted.');
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      return;
    }

    const name = roleNameById(rid);
    const assignedCount = users.reduce((cnt, u) => cnt + (Number(u.Role_Id) === rid ? 1 : 0), 0);

    setRoleToDelete({ id: rid, name, assignedCount, inUse: assignedCount > 0 });
    if (deleteRoleModalRef.current) new BootstrapModal(deleteRoleModalRef.current).show();
  };


  const hideDeleteRoleModal = () => {
    if (deleteRoleModalRef.current) {
      const modalInstance =
        BootstrapModal.getInstance(deleteRoleModalRef.current) || new BootstrapModal(deleteRoleModalRef.current);
      modalInstance.hide();
    }
    setRoleToDelete(null);
  };

  const handleConfirmDeleteRole = async () => {
    if (!roleToDelete?.id || roleToDelete?.inUse) return; // NEW guard
    try {
      await dispatch(deleteRole(roleToDelete.id));
      setPermsRoleId('1');
      setPermsSelection([]);
      setToastMsg(`Role "${roleToDelete.name}" deleted.`);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
      hideDeleteRoleModal();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to delete role';
      setToastMsg(message);
      if (toastElemRef.current) new BootstrapToast(toastElemRef.current).show();
    }
  };

  const selectedRoleAssignedCount = React.useMemo(() => {
    const rid = Number(permsRoleId);
    if (!rid) return 0;
    return users.reduce((cnt, u) => cnt + (Number(u.Role_Id) === rid ? 1 : 0), 0);
  }, [users, permsRoleId]);

  const isDeleting = deletingRoleId === Number(permsRoleId);

  const deleteBtnDisabled =
    !permsRoleId ||
    Number(permsRoleId) === 1 ||
    rolesLoading ||
    isDeleting ||
    selectedRoleAssignedCount > 0;

  const deleteBtnReason = !permsRoleId
    ? 'Select a role first.'
    : Number(permsRoleId) === 1
    ? '“All Access” cannot be deleted.'
    : selectedRoleAssignedCount > 0
    ? `This role is assigned to ${selectedRoleAssignedCount} user(s). Reassign users first.`
    : rolesLoading
    ? 'Roles are still loading.'
    : '';



  // --- render
  return (
    <>
      <div className="container-xxl">
        {/* Create User Modal */}
        <Modal show={showCreateUserModal} onHide={closeCreateUserModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Create User</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="createUserEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  isInvalid={!!email && !isValidEmail(email)}
                  autoComplete="email"
                />
                <Form.Control.Feedback type="invalid">Please provide a valid email address.</Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="createUserPassword">
                <Form.Label>Password</Form.Label>
                <div className="d-flex">
                  <Form.Control
                    type="text"
                    placeholder="Enter or generate a password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    isInvalid={!!password && !isValidPassword(password)}
                    autoComplete="new-password"
                  />
                  <Button variant="outline-secondary" className="ms-2" onClick={() => setPassword(generatePassword())}>
                    Generate
                  </Button>
                </div>
                <Form.Text muted>Minimum 8 characters, include letters and numbers.</Form.Text>

                <Form.Group className="mb-3" controlId="createUserRole" style={{ paddingTop: 5 }}>
                  <Form.Label>Role</Form.Label>
                  <Form.Select
                    value={roleId}
                    onChange={(e) => setRoleId(e.target.value)}
                    disabled={rolesLoading || !roles.length}
                  >
                    <option value="" disabled>
                      {rolesLoading ? 'Loading roles…' : 'Select role'}
                    </option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Control.Feedback type="invalid">
                  Password must be at least 8 characters and contain letters and numbers.
                </Form.Control.Feedback>
              </Form.Group>
            </Form>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={closeCreateUserModal} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateUser} disabled={!formValid || submitting}>
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete User Modal (bootstrap native) */}
        <div
          className="modal fade"
          id="modalDeleteUser"
          tabIndex="-1"
          aria-labelledby="modalDeleteUserLabel"
          aria-hidden="true"
          ref={deleteModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="modalDeleteUserLabel">
                  Delete User
                </h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={hideDeleteModal} />
              </div>
              <div className="modal-body">
                {emailToDelete ? (
                  <>
                    Are you sure you want to delete user <strong>{emailToDelete}</strong>? This action cannot be undone.
                  </>
                ) : (
                  'Are you sure you want to delete this user?'
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-default" onClick={hideDeleteModal}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={handleConfirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal
          title="Create New User"
          visible={showCreateUserModal}
          onCancel={() => setShowCreateUserModal(false)}
          footer={[
            <Button key="cancel" onClick={() => setShowCreateUserModal(false)}>
              Cancel
            </Button>,
            <Button
              key="confirm"
              type="primary"
              disabled={!isValidEmail(email) || !isValidPassword(password)}
              onClick={handleCreateUser}
            >
              Create
            </Button>,
          ]}
        >
          <Form.Control type="text" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Form.Control
            placeholder="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            suffix={
              <Button size="small" onClick={generatePassword}>
                Generate
              </Button>
            }
          />
        </Modal>

        {/* Create Role Modal */}
        <Modal show={showCreateRoleModal} onHide={closeCreateRoleModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Create Role</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3" controlId="createRoleName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., ops, analyst, viewer"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  autoComplete="off"
                />
              </Form.Group>

              <Form.Group className="mb-1" controlId="createRoleDesc">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="What does this role allow?"
                  value={roleDesc}
                  onChange={(e) => setRoleDesc(e.target.value)}
                />
              </Form.Group>
              <Form.Text muted>After creating, select the permissions for this role below.</Form.Text>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeCreateRoleModal} disabled={creatingRole}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateRole} disabled={creatingRole || roleName.trim().length === 0}>
              {creatingRole ? 'Creating…' : 'Create'}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete Role Modal (bootstrap native) */}
        <div
          className="modal fade"
          id="modalDeleteRole"
          tabIndex="-1"
          aria-labelledby="modalDeleteRoleLabel"
          aria-hidden="true"
          ref={deleteRoleModalRef}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="modalDeleteRoleLabel">Delete Role</h5>
                <button type="button" className="btn-close" aria-label="Close" onClick={hideDeleteRoleModal} />
              </div>
                <div className="modal-body">
                  {roleToDelete ? (
                    roleToDelete.inUse ? (
                      <>
                        Role <strong>{roleToDelete.name}</strong> is currently assigned to{' '}
                        <strong>{roleToDelete.assignedCount}</strong> user(s) and <strong>cannot be deleted</strong>.<br />
                        Please reassign those users to another role first.
                      </>
                    ) : (
                      <>
                        Are you sure you want to permanently delete role <strong>{roleToDelete.name}</strong>?<br />
                        This action cannot be undone.
                      </>
                    )
                  ) : (
                    'Are you sure you want to delete this role?'
                  )}
                </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-default" onClick={hideDeleteRoleModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleConfirmDeleteRole}
                  disabled={deletingRoleId === roleToDelete?.id}
                >
                  {deletingRoleId === roleToDelete?.id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-xl-12 col-xxl-11">
            <div className="row">
              <div className="col-xl-9">
                {/* Profile Settings */}
                <section id="changepass" className="mb-5">
                  <h4>
                    <i className="fa fa-user fa-fw text-theme"></i> Profile Settings
                  </h4>
                  <p>Change your account settings.</p>
                  <Card>
                    <div className="list-group list-group-flush">
                      <div className="list-group-item d-flex align-items-center">
                        <div className="flex-1 text-break">
                          <div>Password</div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-default w-100px"
                          data-bs-toggle="modal"
                          data-bs-target="#modalEdit"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </Card>
                </section>

                {/* Dashboard Users (admin only) */}
                {adminFlag === 1 && (
                  <section id="dashboardUsers" className="mb-5">
                    <h4>
                      <i className="fa fa-users fa-fw text-theme"></i> Dashboard Users
                    </h4>
                    <p>Manage users access.</p>
                    <Card>
                      <div className="table-responsive">
                        {usersLoading ? (
                          <p>Loading users...</p>
                        ) : usersError ? (
                          <p className="text-danger">Error: {usersError}</p>
                        ) : (
                          <table className="table mb-0">
                            <thead>
                              <tr>
                                <th>ID</th>
                                <th>Email</th>
                                <th>Last Connected</th>
                                <th>Role</th>
                                <th>Active Flag</th>
                              </tr>
                            </thead>
                            <tbody>
                              {users.map((u) => (
                                <tr key={u.User_Id}>
                                  <td>{u.User_Id}</td>
                                  <td style={{ fontWeight: hoveredEmail === u.Email ? 'bold' : 'normal' }}>{u.Email}</td>
                                  <td>
                                    {u.Last_Connected ? new Date(u.Last_Connected).toLocaleString('en-US') : '-'}
                                  </td>
                                  <td style={{ minWidth: 120 }}>
                                    <Form.Select
                                      size="sm"
                                      value={u.Role_Id ?? ''}
                                      onChange={(e) => handleRoleChange(u.Email, u.Role_Id, e)}
                                      disabled={rolesLoading || updatingRoleEmail === u.Email}
                                      className="form-select listbox"
                                    >
                                      <option value="" disabled>
                                        {rolesLoading ? 'Loading roles…' : 'Select role'}
                                      </option>
                                      {roles.map((role) => (
                                        <option key={role.id} value={role.id}>
                                          {role.name}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </td>
                                  <td>
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div className="form-check form-switch mb-0">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          checked={u.Active_Flag === 1}
                                          onChange={() => handleToggle(u.User_Id, u.Email, u.Active_Flag)}
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        className="btn btn-outline-theme btn-sm me-2"
                                        onClick={() => handleNotify(u.Email)}
                                        onMouseEnter={() => setHoveredEmail(u.Email)}
                                        onMouseLeave={() => setHoveredEmail(null)}
                                      >
                                        Notify
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm me-2"
                                        onClick={() => openDeleteModal(u.Email)}
                                      >
                                        Delete User
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </Card>
                    <div style={{ paddingTop: 20 }}>
                      <button
                        type="button"
                        className="btn btn-outline-default w-150px"
                        onClick={openCreateUserModal}
                      >
                        Create User
                      </button>
                    </div>
                  </section>
                )}

                {/* Role Permissions (admin only) */}
                {adminFlag === 1 && (
                  <section id="rolePermissions" className="mb-5">
                    <h4>
                      <i className="fa fa-shield-alt fa-fw text-theme"></i> Role Permissions
                    </h4>
                    <p>Select a role and manage its permissions.</p>
                    <Card>
                      <div className="p-3">
                        {/* Role select + Delete Role button */}
                        <div className="mb-3" style={{ maxWidth: 560 }}>
                          <Form.Label>Role</Form.Label>
                          <div className="d-flex">
                            <Form.Select
                              value={permsRoleId}
                              onChange={(e) => setPermsRoleId(e.target.value)}
                              disabled={rolesLoading || roles.length === 0}
                              aria-label="Select role for permissions"
                              style={{ maxWidth: 360 }}
                            >
                              <option value="" disabled>
                                {rolesLoading ? 'Loading roles…' : 'Select role'}
                              </option>
                              {roles.map((r) => (
                                <option key={r.id} value={r.id}>
                                  {r.name}
                                </option>
                              ))}
                            </Form.Select>

{deleteBtnDisabled ? (
  <OverlayTrigger
    placement="top"
    overlay={<Tooltip id="tt-del-role">{deleteBtnReason}</Tooltip>}
  >
    <span className="d-inline-block">
      <Button
        variant="outline-danger"
        className="ms-2"
        onClick={openDeleteRoleModal}
        disabled
        style={{ pointerEvents: 'none' }} // let the wrapper catch hover
      >
        Delete Role
      </Button>
    </span>
  </OverlayTrigger>
) : (
  <Button
    variant="outline-danger"
    className="ms-2"
    onClick={openDeleteRoleModal}
    disabled={isDeleting}
  >
    {isDeleting ? 'Deleting…' : 'Delete Role'}
  </Button>
)}


                          </div>
                            {/* selected role description in italics */}
                            {selectedRoleDesc && (
                              <div className="text-muted fst-italic small mt-1" style={{ paddingLeft: 5 }}>
                                {selectedRoleDesc}
                              </div>
                            )}
                        </div>

                        {/* Errors */}
                        {permissionsMasterError && (
                          <div className="text-danger mb-2">Error: {permissionsMasterError}</div>
                        )}
                        {rolePermissionsError && (
                          <div className="text-danger mb-2">Error: {rolePermissionsError}</div>
                        )}
                        {deleteRoleError && (
                          <div className="text-danger mb-2">Error: {deleteRoleError}</div>
                        )}

                        {/* Permissions list */}
                        {permissionsMasterLoading || rolePermissionsLoading ? (
                          <div className="text-muted" style={{ minHeight: '250px'}}>Loading permissions…</div>
                        ) : (
                          <div className="row">
                            {permissionsMaster.map((p) => {
                              const id = Number(p.id);
                              const chk = permsSelection.includes(id);
                              return (
                                <div key={id} className="col-md-4 col-sm-6 mb-2">
                                  <Form.Check
                                    type="checkbox"
                                    id={`perm-${id}`}
                                    label={
                                      <div>
                                        <div className="fw-semibold">{p.code}</div>
                                        {p.description && (
                                          <div className="text-muted fst-italic small mt-1">
                                            {p.description}
                                          </div>
                                        )}
                                      </div>
                                    }
                                    checked={chk}
                                    onChange={() => handlePermToggle(id)}
                                  />
                                </div>
                              );
                            })}
                            {permissionsMaster.length === 0 && (
                              <div className="col-12 text-muted">No permissions defined.</div>
                            )}
                          </div>
                        )}

                        {/* Save */}
                        <div className="mt-3">
                          <Button
                            variant="outline-theme"
                            onClick={handleSaveRolePerms}
                            disabled={
                              !permsRoleId ||
                              replacingRolePermissions === Number(permsRoleId) ||
                              permissionsMasterLoading ||
                              rolePermissionsLoading
                            }
                          >
                            {replacingRolePermissions === Number(permsRoleId) ? 'Saving…' : 'Save Changes'}
                          </Button>
                        </div>
                      </div>
                    </Card>

                    {/* Create Role button BELOW the card*/}
                    <div style={{ paddingTop: 20 }}>
                      <button
                        type="button"
                        className="btn btn-outline-default w-150px"
                        onClick={openCreateRoleModal}
                      >
                        Create Role
                      </button>
                    </div>
                  </section>
                )}
              </div>

              {/* Sidebar nav */}
              <div className="col-xl-3">
                <NavScrollTo>
                  <nav className="nav flex-column">
                    <a className="nav-link" href="#changepass">
                      Profile Settings
                    </a>
                    {adminFlag === 1 && <a className="nav-link" href="#dashboardUsers">Dashboard Users</a>}
                    {adminFlag === 1 && <a className="nav-link" href="#rolePermissions">Role Permissions</a>}
                  </nav>
                </NavScrollTo>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      <div className="modal fade" id="modalEdit" tabIndex="-1" aria-labelledby="modalEditLabel" aria-hidden="true">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalEditLabel">
                Change Password
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
            </div>
            <div className="modal-body">
              {/* Current Password */}
              <div className="mb-3">
                <label className="form-label">Current Password</label>
                <div className="input-group">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="form-control"
                    placeholder="Current Password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div className="mb-3">
                <label className="form-label">New Password</label>
                <div className="input-group">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className="form-control"
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {/* Retype New Password */}
              <div className="mb-3">
                <label className="form-label">Retype New Password</label>
                <div className="input-group">
                  <input
                    type={showNewPassword2 ? 'text' : 'password'}
                    className="form-control"
                    placeholder="Retype New Password"
                    value={newPassword2}
                    onChange={(e) => setNewPassword2(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setShowNewPassword2(!showNewPassword2)}
                  >
                    {showNewPassword2 ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-default" data-bs-dismiss="modal">
                Close
              </button>
              <button type="button" className="btn btn-outline-theme" onClick={handleChangePassword}>
                Change Password
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Toast Container */}
      <div
        className="toast-container position-fixed p-3"
        style={{ top: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 1055 }}
      >
        <div ref={toastElemRef} className="toast" role="alert" aria-live="assertive" aria-atomic="true">
          <div className="toast-header">
            <strong className="me-auto">Notification</strong>
            <button type="button" className="btn-close" data-bs-dismiss="toast" aria-label="Close" />
          </div>
          <div className="toast-body">{toastMsg}</div>
        </div>
      </div>
    </>
  );
}

export default Settings;
